/**
 * useDailyChecks Hook
 *
 * Manages daily check completions with date-based tracking.
 * Stores completion status per day.
 *
 * FT-114: REWRITTEN to use useStorageState directly (same pattern as useDrillsConfig).
 * Removed manual readJSON/writeJSON, custom events, and manual sync listeners.
 * React handles reactivity automatically via useStorageState.
 */

import { useCallback, useEffect } from 'react';
import { useVessel } from '../../../contexts/VesselContext';
import { useStorageState } from '../../useStorageState';
import {
  DEFAULT_DAILY_CHECKS,
  DAILY_CHECK_CATEGORY_ICONS
} from '../../../config/operations/dailyChecksLibrary';
import { useDailyChecksConfig } from './useDailyChecksConfig';

const STORAGE_KEY = 'daily_checks';

/**
 * ONE-TIME MIGRATION: seaready_daily_checks_* → daily_checks_*
 * Required for sync system compatibility (moduleMap expects daily_checks base key)
 */
const migrateLegacyStorageKeys = (vesselId) => {
  if (!vesselId) return;

  const migrationFlag = `daily_checks_migration_done_${vesselId}`;

  // Check if migration already completed for this vessel
  if (localStorage.getItem(migrationFlag)) {
    return;
  }

  const oldKey = `seaready_daily_checks_${vesselId}`;
  const newKey = `daily_checks_${vesselId}`;

  try {
    const oldData = localStorage.getItem(oldKey);

    if (oldData) {
      // Copy to new key (don't delete old key for safety)
      localStorage.setItem(newKey, oldData);
      console.log(`[Daily Checks Migration] Migrated data for vessel ${vesselId}`);
    }

    // Mark migration as complete
    localStorage.setItem(migrationFlag, 'true');

  } catch (error) {
    console.error('[Daily Checks Migration] Failed:', error);
  }
};

export const useDailyChecks = () => {
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();
  const vesselId = vessel?.id;

  // FT-80: Get enabled categories from config (includes user edits and disabled states)
  const { getEnabledCategories } = useDailyChecksConfig();

  // FT-114: Use useStorageState directly - React handles reactivity and sync automatically
  // No more manual readJSON/writeJSON, custom events, or sr:sync:hydrate listeners needed
  const { value: completions, setValue: setCompletions } = useStorageState(STORAGE_KEY, {});

  // Run one-time migration for this vessel (FT-80 Phase 9)
  useEffect(() => {
    if (vesselId) {
      migrateLegacyStorageKeys(vesselId);
    }
  }, [vesselId]);

  // Get today's date key
  const getTodayKey = useCallback(() => {
    return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  }, []);

  // Check if category is completed today
  const isCategoryCompletedToday = useCallback((category) => {
    const todayKey = getTodayKey();
    return completions?.[todayKey]?.[category]?.completed === true;
  }, [completions, getTodayKey]);

  // Get completion time for category today
  const getCategoryCompletionTime = useCallback((category) => {
    const todayKey = getTodayKey();
    return completions?.[todayKey]?.[category]?.completedAt || null;
  }, [completions, getTodayKey]);

  // FT-80: Get checks for a category (respects user edits from config)
  const getChecksForCategory = useCallback((category) => {
    const enabledCats = getEnabledCategories();
    const cat = enabledCats.find(c => c.id === category);
    if (cat && cat.checks) {
      return cat.checks;
    }
    // Fallback to defaults
    return DEFAULT_DAILY_CHECKS[category] || [];
  }, [getEnabledCategories]);

  // Complete a category
  // FT-114: Simplified - setCompletions triggers useStorageState which handles:
  // 1. localStorage write
  // 2. sr:storage:set event for sync
  // 3. React re-render for all consumers
  // No manual saveCompletions or custom events needed
  const completeCategory = useCallback((category, checkResults, completedBy) => {
    const todayKey = getTodayKey();
    const now = new Date().toISOString();

    setCompletions(prev => ({
      ...prev,
      [todayKey]: {
        ...(prev?.[todayKey] || {}),
        [category]: {
          completed: true,
          completedAt: now,
          completedBy: completedBy || 'Unknown',
          checks: checkResults
        }
      }
    }));
  }, [getTodayKey, setCompletions]);

  // FT-80: Get summary for today (respects enabled categories only)
  const getTodaySummary = useCallback(() => {
    const enabledCats = getEnabledCategories();
    const completed = enabledCats.filter(cat => isCategoryCompletedToday(cat.id)).length;
    const total = enabledCats.length;

    return {
      total,
      completed,
      pending: total - completed,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [getEnabledCategories, isCategoryCompletedToday]);

  // FT-80: Get all categories with status (respects enabled/disabled from config)
  const getCategoriesWithStatus = useCallback(() => {
    const enabledCats = getEnabledCategories();
    return enabledCats.map(cat => ({
      id: cat.id,
      label: cat.label,
      icon: DAILY_CHECK_CATEGORY_ICONS[cat.id],
      completed: isCategoryCompletedToday(cat.id),
      completedAt: getCategoryCompletionTime(cat.id),
      checks: cat.checks
    }));
  }, [getEnabledCategories, isCategoryCompletedToday, getCategoryCompletionTime]);

  return {
    // State
    completions,

    // Queries
    isCategoryCompletedToday,
    getCategoryCompletionTime,
    getChecksForCategory,
    getTodaySummary,
    getCategoriesWithStatus,

    // Actions
    completeCategory
  };
};

export default useDailyChecks;
